<template>
<div
    class="poster-image"
    :class="{
        'poster-image-loaded': loaded,
        'poster-image-failed': failed,
        'poster-image-skeleton': skeleton
    }"
    :aria-busy="!loaded && !failed"
    :aria-hidden="skeleton ? 'true' : undefined"
>
    <img
        v-if="src"
        ref="image"
        :src="src"
        :alt="alt"
        draggable="false"
        @load="handleLoad"
        @error="handleError"
    >
</div>
</template>

<script>
let posterLoadingSequence = 0

export default {
    name: 'PosterImage',
    emits: ['load', 'error'],
    props: {
        src: String,
        alt: {
            type: String,
            default: ''
        },
        skeleton: Boolean
    },
    data() {
        return {
            loaded: false,
            failed: false,
            loadingToken: null
        }
    },
    watch: {
        src: {
            immediate: true,
            handler() {
                this.startLoading()
            }
        }
    },
    beforeUnmount() {
        this.finishLoading()
    },
    methods: {
        startLoading() {
            this.finishLoading()
            this.loaded = false
            this.failed = false

            if (!this.src) {
                this.failed = !this.skeleton
                return
            }

            this.loadingToken = `poster-${++posterLoadingSequence}`
            this.$store.commit('BEGIN_POSTER_LOADING', {
                token: this.loadingToken,
                scope: this.$route.fullPath
            })
            this.$nextTick(() => {
                let image = this.$refs.image
                if (!image || !image.complete) return
                if (image.naturalWidth > 0) this.handleLoad()
                else this.handleError()
            })
        },
        handleLoad() {
            this.loaded = true
            this.failed = false
            this.finishLoading()
            this.$emit('load')
        },
        handleError() {
            this.loaded = false
            this.failed = true
            this.finishLoading()
            this.$emit('error')
        },
        finishLoading() {
            if (!this.loadingToken) return
            this.$store.commit('END_POSTER_LOADING', this.loadingToken)
            this.loadingToken = null
        }
    }
}
</script>

<style scoped>
.poster-image {
    position: relative;
    display: inline-block;
    overflow: hidden;
    box-sizing: border-box;
    background: #292929;
    isolation: isolate;
}
.poster-image::after {
    content: '';
    position: absolute;
    z-index: 2;
    inset: 0;
    background: linear-gradient(
        105deg,
        transparent 22%,
        rgba(255, 255, 255, 0.035) 38%,
        rgba(255, 255, 255, 0.13) 50%,
        rgba(255, 255, 255, 0.035) 62%,
        transparent 78%
    );
    transform: translateX(-110%);
    animation: poster-shimmer 1.25s ease-in-out infinite;
    pointer-events: none;
}
.poster-image-loaded::after,
.poster-image-failed::after {
    opacity: 0;
    animation: none;
}
.poster-image img {
    position: relative;
    z-index: 1;
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0;
    transition: opacity 220ms ease;
}
.poster-image-loaded img { opacity: 1; }
.poster-image-failed { background: #242424; }

@keyframes poster-shimmer {
    to { transform: translateX(110%); }
}

@media (prefers-reduced-motion: reduce) {
    .poster-image::after {
        animation: none;
        transform: none;
        background: rgba(255, 255, 255, 0.045);
    }
    .poster-image img { transition: none; }
}
</style>
